import React from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface ConfirmDialogProps {
	open: boolean;
	title: string;
	message: React.ReactNode;
	severity?: "warning" | "info" | "error" | "success";
	confirmButtonText?: string;
	cancelButtonText?: string;
	onConfirm: () => void;
	onCancel: () => void;
	maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
	open,
	title,
	message,
	severity = "warning",
	confirmButtonText = "Confirm",
	cancelButtonText = "Cancel",
	onConfirm,
	onCancel,
	maxWidth = "xs",
}) => {
	const getIcon = () => {
		switch (severity) {
			case "warning":
				return (
					<WarningAmberIcon sx={{ color: "warning.main", fontSize: 36 }} />
				);
			case "error":
				return <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 36 }} />;
			case "success":
				return (
					<CheckCircleOutlineIcon
						sx={{ color: "success.main", fontSize: 36 }}
					/>
				);
			case "info":
			default:
				return <InfoOutlinedIcon sx={{ color: "info.main", fontSize: 36 }} />;
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onCancel}
			maxWidth={maxWidth}
			aria-labelledby="confirm-dialog-title"
			aria-describedby="confirm-dialog-description"
		>
			<DialogTitle id="confirm-dialog-title" sx={{ pb: 1 }}>
				<Box display="flex" alignItems="center" gap={1}>
					{getIcon()}
					{title}
				</Box>
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="confirm-dialog-description">
					{message}
				</DialogContentText>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onCancel} variant="outlined">
					{cancelButtonText}
				</Button>
				<Button
					onClick={onConfirm}
					variant="contained"
					color={severity === "error" ? "error" : "primary"}
					autoFocus
				>
					{confirmButtonText}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ConfirmDialog;
